import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFormConfigurations } from '@/hooks/useFormConfigurations';
import { useSubmissions } from '@/hooks/useSubmissions';
import ConfigurableForm, { resolveOtherAnswer } from '@/components/base/ConfigurableForm';
import type { FormAnswers } from '@/components/base/ConfigurableForm';

export default function ColaboradorIncidente() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { getForm } = useFormConfigurations();
  const { submitIncident } = useSubmissions();

  const config = getForm('incident');

  const initialValues: FormAnswers = {
    'q-inc-1': user?.name || '',
  };

  const handleSubmit = async (answers: FormAnswers) => {
    setSubmitting(true);
    setError('');

    try {
      await submitIncident({
        nome: (answers['q-inc-1'] as string) || user?.name || '',
        setor: resolveOtherAnswer(answers, 'q-inc-2') || '',
        noSetor: resolveOtherAnswer(answers, 'q-inc-3') || '',
        tipoIncidente: resolveOtherAnswer(answers, 'q-inc-4') || '',
        descricao: (answers['q-inc-5'] as string) || '',
        arquivo: (answers['q-inc-6'] as File | null) || null,
        declaracaoVerdade: !!answers['q-inc-7'],
      });

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao registrar incidente. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-portal-gray-500">Formulário não configurado.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div>
        <div className="bg-white rounded-xl border border-portal-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-green-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-portal-gray-900 mb-2">
            OBRIGADA POR COMPARTILHAR CONOSCO.
          </h2>
          <p className="text-sm font-semibold text-green-600 mb-4">
            INCIDENTE REGISTRADO COM SUCESSO!
          </p>
          <p className="text-sm text-portal-gray-500 mb-6">
            Sua segurança é nossa prioridade. A equipe de Segurança do Trabalho analisará o caso.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSubmitted(false); }}
              className="px-4 py-2.5 bg-portal-purple text-white text-sm font-medium rounded-lg hover:bg-portal-purple-dark transition-colors whitespace-nowrap"
            >
              Novo Registro
            </button>
            <button
              onClick={() => navigate('/colaborador/historico')}
              className="px-4 py-2.5 border border-portal-gray-300 text-portal-gray-700 text-sm font-medium rounded-lg hover:bg-portal-gray-50 transition-colors whitespace-nowrap"
            >
              Ver Histórico
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-portal-gray-900 mb-2">Registro de Incidente</h1>
      <p className="text-sm text-portal-gray-500 mb-6">
        Registre ocorrências e incidentes de segurança no ambiente de trabalho.
      </p>

      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden mb-4">
        <div className="px-6 py-4 border-b border-portal-gray-100">
          <p className="text-xs text-portal-gray-400 uppercase tracking-wider font-medium">BEM VINDO COLABORADOR</p>
          <h2 className="text-sm font-bold text-portal-gray-900 mt-1">REGISTRO DE INCIDENTE</h2>
        </div>
        <div className="p-6">
          <div className="bg-portal-purple-50 rounded-lg p-4 mb-4">
            <p className="text-xs text-portal-gray-500 mb-2">Você sabe o que é um incidente?</p>
            <img
              src="https://readdy.ai/api/search-image?query=Industrial%20workplace%20safety%20warning%20sign%20caution%20symbols%20on%20clean%20white%20background%20professional%20corporate%20illustration%20dark%20blue%20and%20gray%20tones%20minimalist%20style&width=600&height=250&seq=incident-info-supabase&orientation=landscape"
              alt="Informação sobre incidentes"
              className="w-full h-32 object-cover rounded-lg"
            />
          </div>
          <p className="text-xs text-portal-gray-400">
            É uma ocorrência inesperada. No entanto, ela é menos grave do que o acidente e, por isso, não causa danos significativos nem ao trabalhador, nem à empresa.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
          <span>{error}</span>
        </div>
      )}

      <ConfigurableForm
        config={config}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitLabel={submitting ?'Enviando...' : 'Enviar'}
        cancelLabel="Limpar formulário"
        onCancel={() => navigate('/colaborador')}
        disabled={submitting}
      />
    </div>
  );
}
